import { useAtomValue } from 'jotai';
import { toast } from 'sonner';
import { XIcon } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@rocco/components/dialog';
import {
    Drawer,
    DrawerContent,
    DrawerDescription,
    DrawerHeader,
    DrawerTitle,
} from '@rocco/components/drawer';
import { Image } from '@rocco/components/image';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import { useEnquirySession } from '~/modules-rocco/enquiry/hooks/use-enquiry';
import { useEnquiryModal } from '~/modules-rocco/enquiry/hooks/use-enquiry-modal';
import { EnquiryForm } from './EnquiryForm';

/**
 * An modal with an enquiry form for users to submit an enquiry to a professional.
 * This component works with the `useEnquiryModal` hook to enable the modal on
 * all pages.
 *
 * There should only be one instance of this component near the root component.
 * This modal should be opened and closed via the `useEnquiryModal` hook.
 * @see {@link useEnquiryModal}
 */
export const EnquiryModal = () => {
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    const { isOpen, context, closeEnquiryModal } = useEnquiryModal();
    const { state } = useEnquirySession();

    return isMobile ? (
        <Drawer
            open={isOpen && !!context}
            // Prevent messing with mobile keyboard input.
            // https://vaul.emilkowal.ski/inputs
            repositionInputs={false}
            onOpenChange={(open) => {
                if (!open) {
                    closeEnquiryModal();
                }
            }}
        >
            {context && (
                <DrawerContent
                    className="bg-gray-50"
                    variant="default"
                    hideDefaultCloseButton
                    limitMaxHeight
                    scrollable
                >
                    <DrawerHeader className="mb-4">
                        <div className="flex items-center gap-x-6">
                            <Image
                                className="size-20 object-contain"
                                style={{
                                    backgroundColor:
                                        context.professional
                                            .LogoBackgroundColor,
                                }}
                                src={context.professional.CompanyLogo}
                                alt={context.professional.Title}
                                width={100}
                                height={100}
                                fit="contain"
                                fadeIn={false}
                            />
                            <DrawerTitle className="grow text-left">
                                {context.title ?? 'Make an Enquiry'}
                            </DrawerTitle>
                        </div>
                        {/* Suppress missing description console warning */}
                        <DrawerDescription className="hidden" aria-hidden />
                    </DrawerHeader>
                    <EnquiryModalForm className="px-4 pb-8" />
                </DrawerContent>
            )}
        </Drawer>
    ) : (
        <Dialog
            open={isOpen && !!context}
            onOpenChange={(open) => {
                if (!open) {
                    closeEnquiryModal();
                }
            }}
        >
            {context && (
                <DialogContent
                    className="max-h-[95vh] w-[60rem] max-w-[95vw] overflow-y-auto bg-gray-50 p-4 md:p-12"
                    onPointerDownOutside={(e) => {
                        // Prevent the modal from closing when the form is
                        // submitting and the user clicks outside the modal.
                        if (state !== 'idle') {
                            e.preventDefault();
                        }
                    }}
                >
                    <DialogHeader className="text-left md:mb-8">
                        <div className="flex items-center gap-x-6 md:gap-x-8">
                            <Image
                                className="size-20 object-contain md:size-24"
                                style={{
                                    backgroundColor:
                                        context.professional
                                            .LogoBackgroundColor,
                                }}
                                src={context.professional.CompanyLogo}
                                alt={context.professional.Title}
                                width={100}
                                height={100}
                                fit="contain"
                                fadeIn={false}
                            />
                            <DialogTitle className="grow">
                                {context.title ?? 'Make an Enquiry'}
                            </DialogTitle>
                        </div>
                        <DialogDescription className="hidden" aria-hidden />
                    </DialogHeader>
                    <EnquiryModalForm />
                </DialogContent>
            )}
        </Dialog>
    );
};

const EnquiryModalForm = ({ className }: { className?: string }) => {
    const { context, closeEnquiryModal, openEnquiryModal } = useEnquiryModal();

    return context ? (
        <EnquiryForm
            className={className}
            professional={{
                ...context.professional,
                CustomEnquiryMessage: undefined,
            }}
            itemType={context.itemType}
            siteTreeID={context.siteTreeID}
            source={context.source ?? 'EnquiryModal'}
            relatedItem={context.relatedItem}
            includeBranch={context.includeBranch}
            isModal
            showCustomEnquiryMessage={false}
            showSuccessMessage={false}
            disableAutoFocus={true}
            onSuccess={() => {
                closeEnquiryModal();
                toast.success('Your enquiry was submitted', {
                    cancel: {
                        label: <XIcon className="size-4" />,
                        onClick: () => {},
                    },
                    cancelButtonStyle: {
                        backgroundColor: 'transparent',
                        minWidth: 0,
                    },
                });
            }}
            // TODO: Don't need to close and reopen when the auth modal
            // uses the Rocco dialog instead of the Aria dialog.
            // The Rocco dialog disables pointer events on the body
            // except for the last dialog/popover, this disables the Aria dialog.
            onFailure={closeEnquiryModal}
            onAuthCancel={openEnquiryModal}
        />
    ) : null;
};
